import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

interface PricingFormData {
  seasonName: string;
  seasonStart: string;
  seasonEnd: string;
  price2Pax: number;
  price4Pax?: number;
  price6Pax?: number;
  price8PlusPax?: number;
  singleSupplement?: number;
  childDiscount?: number;
  currency: string;
  isActive: boolean;
}

interface PricingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  pricing?: {
    id: string;
    seasonName: string;
    seasonStart: Date;
    seasonEnd: Date;
    price2Pax: { toNumber: () => number };
    price4Pax: { toNumber: () => number } | null;
    price6Pax: { toNumber: () => number } | null;
    price8PlusPax: { toNumber: () => number } | null;
    singleSupplement: { toNumber: () => number } | null;
    childDiscount: { toNumber: () => number } | null;
    currency: string;
    isActive: boolean;
  };
  onSuccess?: () => void;
}

export function PricingModal({ open, onOpenChange, packageId, pricing, onSuccess }: PricingModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!pricing;

  const formatDateForInput = (date: Date) => {
    return new Date(date).toISOString().split('T')[0];
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<PricingFormData>({
    defaultValues: pricing
      ? {
          seasonName: pricing.seasonName,
          seasonStart: formatDateForInput(pricing.seasonStart),
          seasonEnd: formatDateForInput(pricing.seasonEnd),
          price2Pax: pricing.price2Pax.toNumber(),
          price4Pax: pricing.price4Pax?.toNumber(),
          price6Pax: pricing.price6Pax?.toNumber(),
          price8PlusPax: pricing.price8PlusPax?.toNumber(),
          singleSupplement: pricing.singleSupplement?.toNumber(),
          childDiscount: pricing.childDiscount?.toNumber(),
          currency: pricing.currency,
          isActive: pricing.isActive,
        }
      : {
          currency: 'USD',
          isActive: true,
          seasonStart: new Date().toISOString().split('T')[0],
          seasonEnd: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.package.addPricing.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const updateMutation = trpc.package.updatePricing.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: PricingFormData) => {
    setIsSubmitting(true);

    // Helper to convert NaN to undefined
    const cleanNumber = (val: number | undefined) =>
      val !== undefined && !isNaN(val) ? val : undefined;

    if (isEditing && pricing) {
      updateMutation.mutate({
        id: pricing.id,
        data: {
          seasonName: data.seasonName,
          seasonStart: data.seasonStart,
          seasonEnd: data.seasonEnd,
          price2Pax: data.price2Pax,
          price4Pax: cleanNumber(data.price4Pax) ?? null,
          price6Pax: cleanNumber(data.price6Pax) ?? null,
          price8PlusPax: cleanNumber(data.price8PlusPax) ?? null,
          singleSupplement: cleanNumber(data.singleSupplement) ?? null,
          childDiscount: cleanNumber(data.childDiscount) ?? null,
          currency: data.currency,
          isActive: data.isActive,
        },
      });
    } else {
      createMutation.mutate({
        packageId,
        seasonName: data.seasonName,
        seasonStart: data.seasonStart,
        seasonEnd: data.seasonEnd,
        price2Pax: data.price2Pax,
        price4Pax: cleanNumber(data.price4Pax),
        price6Pax: cleanNumber(data.price6Pax),
        price8PlusPax: cleanNumber(data.price8PlusPax),
        singleSupplement: cleanNumber(data.singleSupplement),
        childDiscount: cleanNumber(data.childDiscount),
        currency: data.currency,
        isActive: data.isActive,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Pricing' : 'Add Pricing'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the pricing information below.'
              : 'Add seasonal pricing for this package.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Season Info */}
          <div className="space-y-2">
            <Label htmlFor="seasonName">Season Name *</Label>
            <Input
              id="seasonName"
              {...register('seasonName', { required: 'Season name is required' })}
              placeholder="High Season 2025"
            />
            {errors.seasonName && (
              <p className="text-sm text-red-500">{errors.seasonName.message}</p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="seasonStart">Season Start *</Label>
              <Input
                id="seasonStart"
                type="date"
                {...register('seasonStart', { required: 'Start date is required' })}
              />
              {errors.seasonStart && (
                <p className="text-sm text-red-500">{errors.seasonStart.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="seasonEnd">Season End *</Label>
              <Input
                id="seasonEnd"
                type="date"
                {...register('seasonEnd', { required: 'End date is required' })}
              />
              {errors.seasonEnd && (
                <p className="text-sm text-red-500">{errors.seasonEnd.message}</p>
              )}
            </div>
          </div>

          {/* Pricing by Group Size */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Price per Person by Group Size</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="price2Pax">2 Pax *</Label>
                <Input
                  id="price2Pax"
                  type="number"
                  step="0.01"
                  {...register('price2Pax', {
                    required: 'Price for 2 pax is required',
                    valueAsNumber: true,
                  })}
                  placeholder="1500"
                />
                {errors.price2Pax && (
                  <p className="text-sm text-red-500">{errors.price2Pax.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="price4Pax">4 Pax</Label>
                <Input
                  id="price4Pax"
                  type="number"
                  step="0.01"
                  {...register('price4Pax', { valueAsNumber: true })}
                  placeholder="1350"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price6Pax">6 Pax</Label>
                <Input
                  id="price6Pax"
                  type="number"
                  step="0.01"
                  {...register('price6Pax', { valueAsNumber: true })}
                  placeholder="1200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price8PlusPax">8+ Pax</Label>
                <Input
                  id="price8PlusPax"
                  type="number"
                  step="0.01"
                  {...register('price8PlusPax', { valueAsNumber: true })}
                  placeholder="1100"
                />
              </div>
            </div>
          </div>

          {/* Supplements */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="singleSupplement">Single Supplement</Label>
              <Input
                id="singleSupplement"
                type="number"
                step="0.01"
                {...register('singleSupplement', { valueAsNumber: true })}
                placeholder="300"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="childDiscount">Child Discount (%)</Label>
              <Input
                id="childDiscount"
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...register('childDiscount', { valueAsNumber: true })}
                placeholder="25"
              />
            </div>
          </div>

          {/* Currency & Status */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Currency</Label>
              <Combobox
                options={[
                  { value: 'USD', label: 'USD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'GBP', label: 'GBP' },
                  { value: 'TRY', label: 'TRY' },
                ]}
                value={watch('currency') || 'USD'}
                onValueChange={(value) => setValue('currency', value)}
                placeholder="Select currency"
                searchPlaceholder="Search..."
              />
            </div>
            <div className="space-y-2">
              <Label>Active</Label>
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="isActive"
                  {...register('isActive')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="isActive" className="font-normal">
                  Pricing is active
                </Label>
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {(createMutation.error || updateMutation.error) && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error?.message || updateMutation.error?.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Add Pricing'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
